import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Pagination from '../common/Pagination/Pagination';
import { useCustomers } from '../../queries/customerQueries';
import { useDeleteCustomer } from '../../mutations/customerMutations';
import { extractItems, extractPagination } from '../../lib/apiHelpers';

const ViewCustomer = () => {
  const navigate = useNavigate();
  const deleteMutation = useDeleteCustomer();

  // ─── Local State ───────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedSales, setSelectedSales] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());

  // Debounce search term changes to prevent spamming the API
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // ─── React Query: Fetch customers from API (server-side pagination & filter) ──
  const { data: apiData, isLoading, isError } = useCustomers(
    currentPage,
    debouncedSearch,
    selectedAgent,
    selectedSales,
    selectedMonth
  );

  // Unwrap the paginated envelope: { data:[], totalPages, ... }
  const customers  = useMemo(() => extractItems(apiData), [apiData]);
  const pagination = useMemo(() => extractPagination(apiData), [apiData]);
  const totalPages = pagination.totalPages;

  // ─── Extract Filter Options Dynamically ────────────────────────────────────
  const agentOptions = useMemo(() => [...new Set(customers.map((c) => c.customerId).filter(Boolean))], [customers]);
  const monthOptions = useMemo(() => [...new Set(customers.map((c) => c.reminderMonth).filter(Boolean))], [customers]);
  const salesOptions = useMemo(() => [...new Set(customers.map((c) => c.salesMode).filter(Boolean))], [customers]);

  const paginatedCustomers = customers; // server already filtered and paged

  // ─── Row Selection Logic ──────────────────────────────────────────────────
  // Prefer MongoDB _id; fall back to customerId for backwards-compat
  const getRowId = (c) => c.id;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIdsOnPage = paginatedCustomers.map((c) => getRowId(c));
      setSelectedRowIds(new Set([...selectedRowIds, ...allIdsOnPage]));
    } else {
      const newSelected = new Set(selectedRowIds);
      paginatedCustomers.forEach((c) => newSelected.delete(getRowId(c)));
      setSelectedRowIds(newSelected);
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRowIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRowIds(newSelected);
  };

  const isAllSelectedOnPage = useMemo(() => {
    if (paginatedCustomers.length === 0) return false;
    return paginatedCustomers.every((c) => selectedRowIds.has(getRowId(c)));
  }, [paginatedCustomers, selectedRowIds]);

  // ─── Delete Selected Rows ──────────────────────────────────────────────────
  // Deletes all selected customers one by one using the existing hook.
  // Cache auto-refreshes and toast appears via the mutation's onSuccess.
  const handleDeleteSelected = () => {
    const idsToDelete = Array.from(selectedRowIds);
    if (idsToDelete.length === 0) return;

    Promise.all(idsToDelete.map((id) => deleteMutation.mutateAsync(id)))
      .then(() => {
        setSelectedRowIds(new Set()); // clear selection after delete
      })
      .catch(() => {
        // errors are already toasted by axiosInstance + mutation onError
      });
  };

  return (
    <div className="d-flex flex-column w-100 h-100">
      {/* ── Header Strip: Search & Filters — bg color used for separation ── */}
      {/* ══ Enterprise Toolbar ══ */}
      <div className="gtss-toolbar-strip">

        {/* ── Left: Module Title ── */}
        <div className="d-flex flex-column me-1" style={{ flexShrink: 0 }}>
          <span className="gtss-page-title">Customer Management</span>
          <span className="gtss-page-subtitle">Customer Records</span>
        </div>

        {/* ── Divider ── */}
        <div className="gtss-divider"></div>

        {/* ── Search ── */}
        <div className="input-group" style={{ width: '220px' }}>
          <input
            type="text"
            className="form-control"
            placeholder="Search by Name, ID..."
            aria-label="Search by Name or ID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="input-group-text">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0" />
            </svg>
          </span>
        </div>

        {/* ── Divider ── */}
        <div className="gtss-divider"></div>

        {/* ── Filters ── */}
        <div className="d-flex gap-2 align-items-center">
          <select
            className="form-select form-select-dark"
            aria-label="Filter by Agent"
            value={selectedAgent}
            onChange={(e) => {
              setSelectedAgent(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">Agents</option>
            {agentOptions.filter(Boolean).map((cid) => (
              <option key={cid} value={cid}>{cid}</option>
            ))}
          </select>

          <select
            className="form-select form-select-dark"
            aria-label="Filter by Month"
            value={selectedMonth}
            onChange={(e) => {
              setSelectedMonth(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">Month</option>
            {monthOptions.filter(Boolean).map((month) => (
              <option key={month} value={month}>{month}</option>
            ))}
          </select>

          <select
            className="form-select form-select-dark"
            aria-label="Filter by Sales Mode"
            value={selectedSales}
            onChange={(e) => {
              setSelectedSales(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">Sales Mode</option>
            {salesOptions.filter(Boolean).map((mode) => (
              <option key={mode} value={mode}>{mode}</option>
            ))}
          </select>
        </div>

        {/* ── Right: Action Buttons ── */}
        <div className="d-flex align-items-center gap-2 ms-auto">
          {/* Delete button — only visible when rows are checked */}
          {selectedRowIds.size > 0 && (
            <button
              id="delete-selected-btn"
              className="btn btn-danger btn-sm fw-semibold d-flex align-items-center gap-1"
              onClick={handleDeleteSelected}
              disabled={deleteMutation.isPending}
              aria-label={`Delete ${selectedRowIds.size} selected customer(s)`}
            >
              {deleteMutation.isPending ? (
                <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> Deleting...</>
              ) : (
                `Delete (${selectedRowIds.size})`
              )}
            </button>
          )}
        </div>

      </div>

      {/* ── Table Container — fills remaining height ── */}
      <div className="flex-grow-1 overflow-hidden d-flex flex-column gtss-view-container">
        {/* ── Table Container ── */}
        <div className="border rounded-3 overflow-hidden bg-white d-flex flex-column flex-grow-1">
          {isError && (
            <div className="alert alert-danger mb-0 rounded-0 py-2 text-center" role="alert" style={{ fontSize: '14px' }}>
              <strong className="d-block d-sm-inline">Failed to load customers.</strong> Please try again later.
            </div>
          )}
          <div className="table-responsive flex-grow-1">
            <table className="table table-hover align-middle mb-0 gtss-table">

              {/* Header */}
              <thead className="gtss-bg-primary text-white text-uppercase">
                <tr>
                  <th className="py-2 px-2 text-center" style={{ width: '60px' }}>
                    <div className="d-flex align-items-center justify-content-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        aria-label="Select all customers on page"
                        checked={isAllSelectedOnPage}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  <th className="py-2 text-center">S.No</th>
                  <th className="py-2 text-start">Agent</th>
                  <th className="py-2 text-start">Customer Name</th>
                  <th className="py-2 text-start">Owner</th>
                  <th className="py-2 text-center">Mobile No</th>
                  <th className="py-2 text-center">Month</th>
                  <th className="py-2 text-center">Sales</th>
                  <th className="py-2 text-start">Items</th>
                  <th className="py-2 text-center">Status</th>
                </tr>
              </thead>

              {/* Body */}
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="10" className="py-5 text-center">
                      <div className="d-flex justify-content-center align-items-center gap-2">
                        <div className="spinner-border spinner-border-sm" role="status" style={{ color: '#1B4F8A' }}>
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span className="text-muted">Loading customers...</span>
                      </div>
                    </td>
                  </tr>
                ) : paginatedCustomers.length > 0 ? (
                  paginatedCustomers.map((customer, idx) => {
                    const serialNo = (currentPage - 1) * pagination.pageSize + idx + 1;
                    const rowId = getRowId(customer);
                    const isSelected = selectedRowIds.has(rowId);
                    const statusVal = customer.status || customer.entityStatus || (customer.isActive === false ? 'Inactive' : 'Active');
                    const isInactive = String(statusVal).toLowerCase() === 'inactive';
                    return (
                      <tr
                        key={rowId}
                        className={isSelected ? 'table-primary-subtle' : ''}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate('/customer/add', { state: customer })}
                      >
                        <td className="px-2 text-center" onClick={(e) => e.stopPropagation()}>
                          <div className="d-flex align-items-center justify-content-center">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              aria-label={`Select ${customer.customerName}`}
                              checked={isSelected}
                              onChange={() => handleSelectRow(rowId)}
                            />
                          </div>
                        </td>
                        <td className="text-center">{serialNo}</td>
                        <td className="text-start">{customer.customerId}</td>
                        <td className="text-start">{customer.customerName}</td>
                        <td className="text-start">{customer.ownerName}</td>
                        <td className="text-center">{customer.mobileNumber}</td>
                        <td className="text-center">{customer.reminderMonth}</td>
                        <td className="text-center">{customer.salesMode}</td>
                        <td className="text-start text-truncate" style={{ maxWidth: '150px' }}>
                          {customer.items}
                        </td>
                        <td className="text-center">
                          <span className={`badge ${isInactive ? 'bg-danger-subtle text-danger border border-danger-subtle' : 'bg-success-subtle text-success border border-success-subtle'} px-2 py-1`}>
                            {statusVal}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="10" className="py-5 text-center text-muted">
                      <div className="d-flex flex-column align-items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" className="text-secondary" viewBox="0 0 16 16">
                          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                          <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z"/>
                        </svg>
                        <span>No data found.</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>


            </table>
          </div>

          {/* ── Pagination Footer ── */}
          {totalPages >= 1 && (
            <div className="border-top bg-white py-3">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ViewCustomer;
